import {Fragment, useState, type ReactNode} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {Film, Maximize2} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl, CopyUrlButton} from '@/components/shared/CopyableUrl';
import {ClassificationScreenshots} from '@/components/classify/NoteScreenshots';
import {PLATFORM_ID} from '@/lib/platform';
import {youtubeChannelUrl} from '@/lib/youtube';
import {type Submission, type SubmissionPlatformStatus} from '@/api/submissions';
import {useSubmissionHistory} from '@/api/hooks';
import {ImageLightbox} from '@/components/shared/ImageLightbox';
import {ThumbnailHistoryDialog} from '@/components/items/ThumbnailHistoryDialog';
import {SensitiveMediaButton} from '@/components/shared/SensitiveMedia';
import {SubmissionCommentsSection} from '@/components/submissions/SubmissionCommentsSection';
import {CalibrationSection} from '@/components/submissions/CalibrationSection';
import {SubmissionStatusPill} from '@/components/submissions/SubmissionStatusPill';
import {
  SubmissionTimeline,
  buildBasicTimelineEntries,
  buildAdvancedTimelineEntries
} from '@/components/submissions/SubmissionTimeline';
import {cn} from '@/lib/utils';
import {platformStatusTone} from '@/lib/status';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {customerDecisionLabel, customerFeedbackDetails} from '@/lib/customerFeedback';

function HistorySection({title, children}: {readonly title: string; readonly children: ReactNode}) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-muted-foreground text-[10px] tracking-widest uppercase">{title}</h3>
      {children}
    </section>
  );
}

// One row of a detail list, on the design system's Item rather than a hand-measured label column:
// the label is the item's title and the value its description, so a long value wraps under its
// label instead of being truncated with no way to read the rest. Rows sit in an ItemGroup, with an
// ItemSeparator between them.
function DetailItem({label, children}: {readonly label: string; readonly children: ReactNode}) {
  return (
    <Item size="xs" className="px-0">
      <ItemContent>
        <ItemTitle className="text-foreground">{label}</ItemTitle>
        <ItemDescription>{children}</ItemDescription>
      </ItemContent>
    </Item>
  );
}

// Carries its own leading separator: it is only ever appended to a list that already has rows
// above it, and it renders nothing at all when there are no values, so the separator has to be
// conditional on the same test.
function EnrichmentTags({label, values}: {readonly label: string; readonly values: string[]}) {
  if (values.length === 0) {
    return null;
  }

  return (
    <>
      <ItemSeparator />
      <DetailItem label={label}>
        <span className="flex flex-wrap gap-1">
          {values.map((value) => (
            <Badge key={value} variant="outline">
              {value}
            </Badge>
          ))}
        </span>
      </DetailItem>
    </>
  );
}

// Deliberately the most visually prominent thing in the drawer, rendered as its own banner above
// the tabs rather than buried as one more section - the whole point of the status-check feature is
// that an analyst sees what happened to their submission at a glance, not after hunting for it.
function PlatformStatusBanner({
  status,
  submissionType
}: {
  readonly status: SubmissionPlatformStatus;
  readonly submissionType: 'Actor' | 'Content';
}) {
  if (!status.last_checked_at) {
    return (
      <Alert>
        <AlertDescription>Not yet checked for platform status.</AlertDescription>
      </Alert>
    );
  }

  const isRemoved = status.status === 'removed';
  const yesNo = (value: boolean | null) => (value === null ? '—' : value ? 'Yes' : 'No');

  // Content (video) and Actor (channel) submissions surface a different, non-overlapping set of
  // attributes - each only ever populates for its own type. All of them are YouTube Data API
  // concepts (monetized, age restricted, family safe...) with no Pinterest equivalent - the Pinterest
  // status checks never populate them, so Falcon, for a profile or a pin alike, would show a wall of
  // permanent "—" placeholders instead.
  const attributes: Array<{label: string; value: string}> =
    PLATFORM_ID === 'pinterest'
      ? []
      : submissionType === 'Actor'
        ? [
            {label: 'Monetized', value: yesNo(status.is_channel_monetized)},
            {label: 'Verified', value: yesNo(status.verified)},
            {label: 'Family safe', value: yesNo(status.is_family_safe)}
          ]
        : [
            {label: 'Age restricted', value: yesNo(status.age_restricted)},
            {label: 'YPP (monetized)', value: yesNo(status.is_channel_monetized)},
            {label: 'Content warning', value: yesNo(status.content_warning)},
            {label: 'Made for kids', value: yesNo(status.made_for_kids)},
            {label: 'Safe for minors', value: yesNo(status.safe_for_minors)},
            {label: 'Verified', value: yesNo(status.verified)},
            {label: 'Restricted regions', value: status.restricted_regions ?? '—'}
          ];

  return (
    <Alert variant={isRemoved ? 'destructive' : 'default'}>
      <AlertTitle className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5">
          <StatusIcon tone={platformStatusTone(status.status)} />
          <span className={cn('capitalize', isRemoved && 'text-destructive-ink')}>
            {status.status ?? 'Unknown'}
          </span>
        </span>
        <span className="text-muted-foreground ml-auto font-normal whitespace-nowrap">
          Checked {formatDistanceToNow(new Date(status.last_checked_at), {addSuffix: true})}
        </span>
      </AlertTitle>
      <AlertDescription>
        {attributes.length > 0 ? (
          // Two columns: every value is a Yes/No/dash, and a single column of seven would make the
          // banner taller than the detail it is meant to sit above.
          <ItemGroup className="grid grid-cols-2">
            {attributes.map((attribute) => (
              <Item key={attribute.label} size="xs" className="px-0 py-0">
                <ItemContent>
                  <ItemTitle className="text-foreground">{attribute.label}</ItemTitle>
                  <ItemDescription>{attribute.value}</ItemDescription>
                </ItemContent>
              </Item>
            ))}
          </ItemGroup>
        ) : null}
        {isRemoved && status.raw_message ? (
          <p className="text-destructive-ink">{status.raw_message}</p>
        ) : null}
        {isRemoved && status.last_check_kind === 'pre-submission' ? (
          <p className="text-destructive-ink">
            Found gone when we tried to submit it - no report was filed.
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

// The historic-row counterpart to the live Overview tab below. A historic row has no
// submission_stream/item/job/classification/timeline - everything worth showing is already on
// `sub` itself (bulk-imported, and, for a subset of rows, later hydrated by an Import:Mode=Enrich
// pass with risk/notes/language/topic/location), so this needs no fetch of its own.
function HistoricSubmissionOverview({sub}: {readonly sub: Submission}) {
  return (
    <div className="space-y-4">
      <HistorySection title="Imported">
        <ItemGroup>
          <DetailItem label="Submitted">
            {formatDistanceToNow(new Date(sub.created_at), {addSuffix: true})}
          </DetailItem>
          {sub.delivered_at ? (
            <>
              <ItemSeparator />
              <DetailItem label="Delivered">
                {formatDistanceToNow(new Date(sub.delivered_at), {addSuffix: true})}
              </DetailItem>
            </>
          ) : null}
          <ItemSeparator />
          <DetailItem label="Submitted by">{sub.created_by}</DetailItem>
        </ItemGroup>
      </HistorySection>

      {sub.customer_feedback ? (
        <HistorySection title="Customer feedback">
          <ItemGroup>
            <DetailItem label="Decision">
              {customerDecisionLabel(sub.customer_feedback.last_decision) ?? '—'}
            </DetailItem>
            {customerFeedbackDetails(sub.customer_feedback).map(({label, value}) => (
              <DetailItem key={label} label={label}>
                {value}
              </DetailItem>
            ))}
            <DetailItem label="Flagged to customer">{sub.customer_feedback.flag_day}</DetailItem>
            <DetailItem label="Feedback received">
              {formatDistanceToNow(new Date(sub.customer_feedback.uploaded_at), {addSuffix: true})}
              {' by '}
              {sub.customer_feedback.uploaded_by}
            </DetailItem>
          </ItemGroup>
        </HistorySection>
      ) : null}

      <HistorySection title="Classification">
        <ItemGroup>
          <DetailItem
            label={sub.risks.length > 1 ? 'Policy Violation / Risks' : 'Policy Violation / Risk'}>
            {sub.policy_violation}
            {sub.risks.length > 0 ? ` / ${sub.risks.join(', ')}` : ''}
          </DetailItem>
          {sub.notes ? (
            <>
              <ItemSeparator />
              <DetailItem label="Notes">{sub.notes}</DetailItem>
            </>
          ) : null}
          <EnrichmentTags label="Language" values={sub.language} />
          <EnrichmentTags label="Topic" values={sub.topic} />
          <EnrichmentTags label="Location" values={sub.location} />
        </ItemGroup>
      </HistorySection>
    </div>
  );
}

const ORIGIN_LABEL: Record<string, string> = {
  moderation_queue: 'Moderation Queue',
  single_submission: 'Single Submission',
  image_search: 'Image Search',
  follower_sweep: 'Follower Sweep',
  investigations: 'Investigations'
};

// The submission's detail - platform status, classification, published/collected, P0 links,
// calibration and reviewer comments - in a right-hand drawer rather than expanded inline in the
// table row. That keeps the table itself scannable, gives the detail a full column of width to
// lay out in (two textareas among the sections were fighting for a table-column's worth of space),
// and drops the sideways scroll a colSpan cell inherited from the table.
export function SubmissionDetailSheet({
  sub,
  onOpenChange,
  onCalibrated,
  onCommentAdded,
  onLastCommentDeleted
}: {
  readonly sub: Submission | null;
  readonly onOpenChange: (open: boolean) => void;
  // Flip the table row's badges the moment the action that earns them succeeds - the history
  // timeline and comment thread below are their own queries, invisible to the row until a full
  // list refetch, and the whole point of a row badge is that it's already true at a glance.
  readonly onCalibrated?: (id: string, completed: boolean) => void;
  readonly onCommentAdded?: (id: string) => void;
  readonly onLastCommentDeleted?: (id: string) => void;
}) {
  return (
    <Sheet open={sub !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 data-[side=right]:sm:max-w-xl">
        {sub ? (
          <SubmissionDetailBody
            sub={sub}
            onCalibrated={(completed) => onCalibrated?.(sub.id, completed)}
            onCommentAdded={() => onCommentAdded?.(sub.id)}
            onLastCommentDeleted={() => onLastCommentDeleted?.(sub.id)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function SubmissionDetailBody({
  sub,
  onCalibrated,
  onCommentAdded,
  onLastCommentDeleted
}: {
  readonly sub: Submission;
  readonly onCalibrated: (completed: boolean) => void;
  readonly onCommentAdded: () => void;
  readonly onLastCommentDeleted: () => void;
}) {
  // A historic row has no submission_stream, so /submissions/{id}/history - keyed on StreamId -
  // 404s for it every time; skip the call rather than firing a request that can only ever fail.
  const {data: history, isLoading} = useSubmissionHistory(sub.id, !sub.is_historic);
  const [timelineMode, setTimelineMode] = useState<'basic' | 'advanced'>('basic');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxRevealed, setLightboxRevealed] = useState(false);
  const [lightboxColourised, setLightboxColourised] = useState(false);
  const [thumbnailHistoryOpen, setThumbnailHistoryOpen] = useState(false);

  const displayName = sub.display_name ?? sub.handle ?? sub.item_external_id ?? sub.content_id;

  return (
    <>
      {/* gap-2 only: the rule under the header and the clearance for the close button are the
          3.0 SheetHeader's own, and the three stacked rows here want more than its gap-0.5. */}
      <SheetHeader className="gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline">{sub.policy_violation}</Badge>
          <Badge variant="secondary">{sub.submission_type}</Badge>
          <SubmissionStatusPill status={sub.action_status} />
        </div>
        <SheetTitle className="break-words">{displayName}</SheetTitle>
        <SheetDescription className="sr-only">
          The submitted item, its classification and its delivery timeline.
        </SheetDescription>
        <div className="-ml-2 flex flex-wrap items-center gap-1">
          {sub.source_url ? <CopyableUrl url={sub.source_url} className="px-2 text-xs" /> : null}
          <CopyButton
            variant="ghost"
            size="xs"
            className="text-muted-foreground"
            label="Copy ID"
            value={sub.item_external_id ?? sub.content_id}
          />
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {sub.is_historic ? (
          <HistoricSubmissionOverview sub={sub} />
        ) : isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Spinner />
            Loading history…
          </div>
        ) : history ? (
          <>
            <PlatformStatusBanner
              status={history.platform_status}
              submissionType={history.submission_type}
            />

            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-4">
                  <HistorySection title="Details">
                    <ItemGroup>
                      <Item size="xs" className="px-0">
                        <ItemMedia>
                          {history.item?.thumbnail_url ? (
                            <div className="flex flex-col items-center gap-1">
                              <SensitiveMediaButton
                                compact
                                label="View thumbnail"
                                src={history.item.thumbnail_url}
                                alt={history.item.title ?? 'Submission thumbnail'}
                                className="border-border size-20 shrink-0 overflow-hidden rounded-md border"
                                imageClassName="h-full w-full object-cover"
                                onClick={(blurred, greyed) => {
                                  setLightboxRevealed(!blurred);
                                  setLightboxColourised(!greyed);
                                  setLightboxOpen(true);
                                }}>
                                <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/40">
                                  <Maximize2 className="size-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                                </span>
                              </SensitiveMediaButton>
                              <Button
                                variant="link"
                                size="xs"
                                onClick={() => {
                                  setThumbnailHistoryOpen(true);
                                }}>
                                Thumbnail history
                              </Button>
                              {thumbnailHistoryOpen ? (
                                <ThumbnailHistoryDialog
                                  open
                                  itemId={history.item.id}
                                  submittedAt={sub.created_at}
                                  onClose={() => {
                                    setThumbnailHistoryOpen(false);
                                  }}
                                />
                              ) : null}
                              <ImageLightbox
                                open={lightboxOpen}
                                revealed={lightboxRevealed}
                                colourised={lightboxColourised}
                                imageUrl={history.item.thumbnail_url}
                                alt={history.item.title ?? 'Submission thumbnail'}
                                onClose={() => {
                                  setLightboxOpen(false);
                                }}
                              />
                            </div>
                          ) : (
                            <div className="text-muted-foreground bg-muted border-border flex size-20 shrink-0 items-center justify-center rounded-md border">
                              <Film className="size-5" />
                            </div>
                          )}
                        </ItemMedia>
                        <ItemContent className="self-start">
                          {history.item ? (
                            <ItemTitle>
                              {/* On a span: ItemTitle is a flex row, and line-clamp needs a block. */}
                              <span className="line-clamp-2 break-words">
                                {history.item.title ?? 'Untitled'}
                              </span>
                            </ItemTitle>
                          ) : (
                            <ItemDescription>No item on record.</ItemDescription>
                          )}
                        </ItemContent>
                      </Item>
                      {history.item ? (
                        <>
                          <ItemSeparator />
                          <DetailItem label="Published">
                            {history.item.published_at
                              ? formatDistanceToNow(new Date(history.item.published_at), {
                                  addSuffix: true
                                })
                              : 'Unknown'}
                          </DetailItem>
                          <ItemSeparator />
                          <DetailItem label="Channel">
                            {history.item.author_id ? (
                              <span className="break-all">
                                {history.item.author_name ? `${history.item.author_name} · ` : null}
                                <span className="font-mono">{history.item.author_id}</span>
                                {PLATFORM_ID === 'youtube' ? (
                                  <CopyUrlButton
                                    url={youtubeChannelUrl(history.item.author_id)}
                                    label="Copy channel URL"
                                    className="ml-0.5 align-middle"
                                  />
                                ) : null}
                              </span>
                            ) : (
                              'Unknown'
                            )}
                          </DetailItem>
                          <ItemSeparator />
                          <DetailItem label="Collected">
                            {formatDistanceToNow(new Date(history.item.collected_at), {
                              addSuffix: true
                            })}
                          </DetailItem>
                          <ItemSeparator />
                          <DetailItem label="Via job">
                            {history.job ? (
                              `${history.job.search_term ?? history.job.source} · ${history.job.created_by}`
                            ) : (
                              <span className="italic">Manually submitted</span>
                            )}
                          </DetailItem>
                        </>
                      ) : null}
                    </ItemGroup>
                  </HistorySection>

                  <HistorySection title="Classification">
                    {history.classification ? (
                      <ItemGroup>
                        <DetailItem label="Analyst">
                          {history.classification.analyst_email}
                        </DetailItem>
                        <ItemSeparator />
                        <DetailItem label="Policy Violation / Risk">
                          {history.classification.policy_violation} /{' '}
                          {history.classification.risks.join(', ')}
                        </DetailItem>
                        <ItemSeparator />
                        <DetailItem label="Via">
                          <Badge variant="outline">
                            {ORIGIN_LABEL[history.classification.origin] ?? 'Investigations'}
                          </Badge>
                        </DetailItem>
                        {history.classification.notes ? (
                          <>
                            <ItemSeparator />
                            <DetailItem label="Notes">{history.classification.notes}</DetailItem>
                          </>
                        ) : null}
                        <ClassificationScreenshots classificationId={history.classification.id} />
                        <EnrichmentTags label="Language" values={sub.language} />
                        <EnrichmentTags label="Topic" values={sub.topic} />
                        <EnrichmentTags label="Location" values={sub.location} />
                        <EnrichmentTags label="Core Risk" values={sub.core_risk} />
                      </ItemGroup>
                    ) : (
                      <p className="text-muted-foreground text-xs">Not yet classified.</p>
                    )}
                  </HistorySection>

                  <HistorySection title="P0 Alerts">
                    {history.p0_alerts.length === 0 ? (
                      <p className="text-muted-foreground text-xs">Not linked to any P0 alert.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {history.p0_alerts.map((alert) => {
                          const badge = (
                            <Badge variant="destructive">
                              {alert.subject ?? 'Untitled P0 Alert'} · {alert.status}
                            </Badge>
                          );
                          // The badge clips a long subject, so the full one sits in a tooltip -
                          // a real one, which keyboard and touch reach where a title never did.
                          return alert.subject ? (
                            <Tooltip key={alert.id}>
                              <TooltipTrigger render={badge} />
                              <TooltipContent className="max-w-xs">{alert.subject}</TooltipContent>
                            </Tooltip>
                          ) : (
                            <Fragment key={alert.id}>{badge}</Fragment>
                          );
                        })}
                      </div>
                    )}
                  </HistorySection>

                  <HistorySection title="Calibration Notes">
                    <CalibrationSection
                      submissionId={sub.id}
                      timeline={history.timeline}
                      onRecorded={onCalibrated}
                    />
                  </HistorySection>

                  <HistorySection title="Reviewer Comments">
                    <SubmissionCommentsSection
                      submissionId={sub.id}
                      onAdded={onCommentAdded}
                      onLastDeleted={onLastCommentDeleted}
                    />
                  </HistorySection>
                </div>
              </TabsContent>

              <TabsContent value="timeline">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <ToggleGroup
                    variant="outline"
                    spacing={0}
                    size="sm"
                    aria-label="Timeline detail"
                    value={[timelineMode]}
                    onValueChange={(next) => {
                      const [mode] = next as Array<'basic' | 'advanced'>;
                      if (mode) {
                        setTimelineMode(mode);
                      }
                    }}>
                    <ToggleGroupItem value="basic">Basic</ToggleGroupItem>
                    <ToggleGroupItem value="advanced">Advanced</ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <SubmissionTimeline
                  entries={
                    timelineMode === 'basic'
                      ? buildBasicTimelineEntries(history)
                      : buildAdvancedTimelineEntries(history.timeline)
                  }
                />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <p className="text-muted-foreground text-xs">No history available.</p>
        )}
      </div>
    </>
  );
}
